import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import formatMessage from 'format-message'
import {
  Badge,
  BulkActionToolbar,
  Button,
  FloatingBarStack,
  InlineConfirm,
  InlineEdit,
  InlineLink,
  Menu,
  Pagination,
  RowExpand,
  SegmentedControl,
  SortHeader,
  StickySaveBar,
  Switch,
  ToggleButton,
} from '@CommonComponents/buttons'

const roles = ['primary', 'secondary', 'ghost', 'danger']
const feedback = {
  'data-load': 'success',
  'data-busy-label': 'Saving…',
  'data-success-label': 'Changes saved',
  'data-failure-label': 'Changes could not be saved',
  'data-success-text': 'Saved!',
  'data-failure-text': 'Try again',
}

// Simulated requests for the reference examples.
const later = (value, fail = false) =>
  new Promise((resolve, reject) =>
    setTimeout(
      () => (fail ? reject(new Error('Simulated failure')) : resolve(value)),
      700
    )
  )

export function TooltipReference() {
  const host = useRef(null)
  const [shadow, setShadow] = useState(null)
  const name = formatMessage('Copy link')
  const explanation = formatMessage('save your changes first')
  const textExplanation = formatMessage('Choose a course before exporting')
  const summary = formatMessage('Updated today')
  useEffect(() => {
    const root =
      host.current.shadowRoot || host.current.attachShadow({ mode: 'open' })
    document
      .querySelectorAll('link[rel="stylesheet"]')
      .forEach(link => root.appendChild(link.cloneNode()))
    setShadow(root)
  }, [])
  const icon = (extra = {}) => (
    <Button
      className="me-btn-icon me-btn-icon-neutral"
      icon="fa-regular fa-copy"
      aria-label={name}
      data-tooltip={name}
      {...extra}
    />
  )
  const row = prefix => (
    <>
      <a
        className="me-btn-link-inline"
        href="#reference-content"
        data-tooltip-summary={summary}
        aria-describedby={prefix + '-summary'}
      >
        {formatMessage('Financial accounting')}
      </a>
      <span id={prefix + '-summary'} hidden>
        {summary}
      </span>
    </>
  )
  return (
    <section aria-label={formatMessage('Tooltips')}>
      <h3>{formatMessage('Tooltips')}</h3>
      <div className="reference-row">
        {icon()}
        {icon({
          'aria-disabled': true,
          'data-tooltip-explanation': explanation,
          'aria-describedby': 'react-tooltip-blocked',
        })}
        <span id="react-tooltip-blocked" hidden>
          {explanation}
        </span>
        <Button
          aria-disabled
          data-tooltip={textExplanation}
          aria-describedby="react-tooltip-text"
        >
          {formatMessage('Export')}
        </Button>
        <span id="react-tooltip-text" hidden>
          {textExplanation}
        </span>
        <span
          aria-hidden="true"
          data-tooltip={formatMessage('Drag to reorder')}
        >
          <i className="fa-regular fa-grip-dots-vertical" aria-hidden="true" />
        </span>
        {row('react')}
      </div>
      <div
        style={{ overflow: 'auto', height: 100 }}
        aria-label={formatMessage('Scrollable example')}
      >
        {icon()}
        <div style={{ height: 240 }} />
      </div>
      <dialog
        open
        style={{ position: 'relative' }}
        aria-label={formatMessage('Tooltip dialog')}
      >
        {icon()}
      </dialog>
      <div ref={host} />
      {shadow &&
        createPortal(
          <>
            {icon()}
            {row('shadow')}
          </>,
          shadow
        )}
    </section>
  )
}

export const App = () => {
  const [page, setPage] = useState(1)
  const [smallPage, setSmallPage] = useState(1)
  const [rate, setRate] = useState(20)
  const [sort, setSort] = useState('none')
  const [expanded, setExpanded] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [count, setCount] = useState(0)
  const [badge, setBadge] = useState(true)
  const [reject, setReject] = useState(false)
  const [grades, setGrades] = useState(false)
  const [dueDates, setDueDates] = useState(false)
  const [bookmarked, setBookmarked] = useState(false)
  const [view, setView] = useState('list')
  const [order, setOrder] = useState('newest')
  const [picked, setPicked] = useState('list')
  const [title, setTitle] = useState('Draft title')
  // Commits the value only when the simulated request succeeds.
  const change = commit => value =>
    later(value, reject).then(() => commit(value))
  // Focus moves to the next control, or the previous one at the end.
  const removeBadge = event => {
    const stops = window.MEButtons.tabStops(document)
    const index = stops.indexOf(event.currentTarget)
    const next = stops[index + 1] || stops[index - 1]
    next?.focus()
    setBadge(false)
  }
  return (
    <>
      <h2>React controls</h2>
      <TooltipReference />
      {[
        ['', 'Default'],
        ['me-btn-sm', 'Small'],
        ['me-btn-xs', 'Extra small'],
      ].map(([size, label]) => (
        <React.Fragment key={label}>
          <h3>{label}</h3>
          <div className="reference-row">
            {roles.map(role => (
              <Button key={role} className={`me-btn me-btn-${role} ${size}`}>
                {role[0].toUpperCase() + role.slice(1)}
              </Button>
            ))}
          </div>
        </React.Fragment>
      ))}
      <h3>Contrast</h3>
      <div className="reference-row reference-contrast" data-surface="contrast">
        {roles.map(role => (
          <Button key={role} className={`me-btn me-btn-${role}`}>
            {role[0].toUpperCase() + role.slice(1)}
          </Button>
        ))}
        {['', 'primary', 'secondary', 'danger', 'neutral'].map(tone => (
          <Button
            key={tone}
            className={`me-btn-icon ${tone ? `me-btn-icon-${tone}` : ''}`}
            icon="fa-regular fa-box-archive"
            aria-label={`Contrast archive ${tone || 'default'}`}
            data-tooltip={`Contrast archive ${tone || 'default'}`}
          />
        ))}
      </div>
      <h3>State and feedback</h3>
      <div className="reference-row">
        <Button className="me-btn me-btn-primary" disabled>
          Unavailable
        </Button>
        <Button aria-disabled="true" data-tooltip="Requires permission">
          Requires permission
        </Button>
        <Button
          {...feedback}
          className="me-btn me-btn-primary"
          onAction={() => later()}
        >
          Save
        </Button>
        <Button
          {...feedback}
          data-load="none"
          onAction={() => later(undefined, true)}
        >
          Try failure
        </Button>
      </div>
      <h3>Selection</h3>
      <label>
        <input
          type="checkbox"
          checked={reject}
          onChange={event => setReject(event.target.checked)}
        />{' '}
        Reject state changes
      </label>
      <Switch
        {...feedback}
        label="Show grades"
        description="Example setting"
        checked={grades}
        onChange={change(setGrades)}
      />
      <Switch
        {...feedback}
        large
        label="Show due dates"
        checked={dueDates}
        onChange={change(setDueDates)}
      />
      <ToggleButton
        {...feedback}
        data-pressed-icon="fa-solid fa-bookmark"
        data-unpressed-icon="fa-regular fa-bookmark"
        data-pressed-label="Saved"
        data-unpressed-label="Save"
        pressed={bookmarked}
        onChange={change(setBookmarked)}
      >
        Save
      </ToggleButton>
      <SegmentedControl
        {...feedback}
        label="React view"
        value={view}
        onChange={change(setView)}
        options={[
          { label: 'List', value: 'list' },
          { label: 'Grid', value: 'grid' },
          { label: 'Compact', value: 'compact', disabled: true },
        ]}
      />
      <SegmentedControl
        {...feedback}
        small
        label="React sort order"
        value={order}
        onChange={change(setOrder)}
        options={[
          { label: 'Newest', value: 'newest' },
          { label: 'Oldest', value: 'oldest' },
        ]}
      />
      <h3>Pagination</h3>
      <Pagination
        currentPage={page}
        rate={rate}
        total={4560}
        setCurrentPage={setPage}
        userId="reference"
        tableId="react"
        setRate={value => {
          setRate(value)
          setPage(1)
        }}
      />
      <Pagination
        small
        currentPage={smallPage}
        rate={20}
        total={180}
        setCurrentPage={setSmallPage}
      />
      <h3>Native semantics</h3>
      <Menu label="Actions">
        <Button className="me-menu-item">Copy</Button>
        <Button className="me-menu-item" href="?runtime=server">
          Server-rendered examples
        </Button>
        <div className="me-menu-sep" role="separator" />
        <Button
          className="me-menu-item is-danger"
          icon="fa-regular fa-trash-can me-menu-ico"
          data-confirm=""
        >
          Delete example…
        </Button>
      </Menu>
      <Button href="?runtime=server">Go to server-rendered examples</Button>
      <p>
        Read the{' '}
        <Button href="?runtime=server" className="me-btn-link-inline">
          server-rendered examples
        </Button>{' '}
        in this reference.
      </p>
      <InlineLink href="https://www.myeducator.com" external>
        MyEducator
      </InlineLink>
      <h3>Icon sizes and request variations</h3>
      <div className="reference-row">
        {['lg', '', 'sm', 'xs'].map(size => (
          <Button
            key={size}
            className={`me-btn-icon ${size ? `me-btn-icon-${size}` : ''}`}
            icon="fa-regular fa-copy"
            aria-label={`Copy ${size || 'default'}`}
            data-tooltip={`Copy ${size || 'default'}`}
          />
        ))}
      </div>
      <div className="reference-row">
        {['', 'primary', 'secondary', 'danger', 'neutral'].map(tone => (
          <Button
            key={tone}
            className={`me-btn-icon ${tone ? `me-btn-icon-${tone}` : ''}`}
            icon="fa-regular fa-box-archive"
            aria-label={`Archive ${tone || 'default'}`}
            data-tooltip={`Archive ${tone || 'default'}`}
          />
        ))}
      </div>
      <div className="reference-row">
        <Button
          {...feedback}
          icon="fa-regular fa-upload"
          data-success-dwell="1000"
          data-failure-dwell="1000"
          data-resolve-label="Uploaded"
          onAction={() => later()}
        >
          Upload
        </Button>
        <Button
          {...feedback}
          id="react-cancel-request"
          data-busy-timeout="1000"
          onAction={() => new Promise(() => {})}
        >
          Timeout example
        </Button>
        <Button data-cancel-load="react-cancel-request">Cancel request</Button>
        <Button
          {...feedback}
          data-abort=""
          data-abort-label="Stop export"
          onAction={() => new Promise(() => {})}
        >
          Export
        </Button>
      </div>
      <h3>Value picker, nested menu and panel</h3>
      <div className="reference-row">
        <Menu
          {...feedback}
          mode="listbox"
          label="View"
          placeholder="Choose a view…"
          swatch="var(--brand)"
          selected={picked}
          onSelect={change(setPicked)}
        >
          <Button data-value="list" data-label="List" className="me-menu-item">
            List
          </Button>
          <Button data-value="grid" data-label="Grid" className="me-menu-item">
            Grid
          </Button>
        </Menu>
        <Menu label="Nested actions">
          <Menu label="Share" submenu>
            <Menu label="Destinations" submenu>
              <Button className="me-menu-item">Copy link</Button>
            </Menu>
          </Menu>
        </Menu>
        <Menu label="More view actions" iconOnly align="end" placement="above">
          <Button className="me-menu-item">Copy view</Button>
        </Menu>
        <Menu label="Details" mode="panel">
          <p>Panel content</p>
          <Button href="#reference-content">Return to reference</Button>
        </Menu>
        <Menu
          label="Page help"
          mode="panel"
          iconOnly
          icon="fa-regular fa-circle-question"
        >
          <p>
            Use these examples to explore button states and keyboard
            interactions.
          </p>
          <InlineLink href="#reference-content">Return to examples</InlineLink>
        </Menu>
      </div>
      <h3>Navigation and split controls</h3>
      <div className="reference-row">
        <Menu className="me-btn me-btn-nav" label="Course">
          <Button
            className="me-menu-item"
            icon="fa-regular fa-book-open me-menu-ico"
          >
            Overview
          </Button>
          <Button
            className="me-menu-item"
            icon="fa-regular fa-folder me-menu-ico"
          >
            Resources
          </Button>
          <Button
            className="me-menu-item"
            icon="fa-regular fa-chart-simple me-menu-ico"
          >
            Reports
          </Button>
        </Menu>
        <span className="me-btn-split">
          <Button className="me-btn me-btn-primary">Create</Button>
          <Menu
            iconOnly
            icon="fa-regular fa-chevron-down me-btn-caret"
            className="me-btn me-btn-primary"
            label="More create actions"
          >
            <Button className="me-menu-item">Create from template</Button>
          </Menu>
        </span>
      </div>
      <h3>Inline editing and confirmation</h3>
      <div className="reference-row">
        <InlineEdit {...feedback} value={title} onSave={change(setTitle)}>
          <input aria-label="Title" />
        </InlineEdit>
        <InlineConfirm
          data-inline-confirm="Remove attachment?"
          data-inline-verb="Remove"
          data-inline-cancel="Keep attachment"
          data-inline-done="Attachment removed"
          data-busy-label="Removing…"
          data-failure-label="Could not remove attachment"
          data-failure-text="Try again"
          onConfirm={() => later()}
        >
          Remove attachment
        </InlineConfirm>
      </div>
      <h3>Table chrome and badges</h3>
      <table className="reference-table">
        <thead>
          <tr>
            <SortHeader
              sort={sort}
              failureLabel="Could not sort names"
              onSort={() =>
                later(
                  {
                    none: 'ascending',
                    ascending: 'descending',
                    descending: 'none',
                  }[sort]
                ).then(setSort)
              }
            >
              Name
            </SortHeader>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <RowExpand
                label="Financial Accounting"
                controls="react-row-details"
                expanded={expanded}
                onChange={setExpanded}
              />
              Financial Accounting
            </td>
          </tr>
          <tr id="react-row-details" hidden={!expanded}>
            <td>Row details</td>
          </tr>
        </tbody>
      </table>
      <div className="reference-row">
        {['brand', 'neutral', 'success', 'danger', 'warning', 'info'].map(
          family => (
            <Badge key={family} family={family}>
              {family}
            </Badge>
          )
        )}
        {badge && <Badge onRemove={removeBadge}>Active</Badge>}
        <Badge disabled>Unavailable</Badge>
      </div>
      <h3>Mobile width and floating bars</h3>
      <div
        className="reference-row reference-header"
        role="group"
        aria-label="Header actions"
      >
        <Menu
          label="More actions"
          iconOnly
          className="me-btn-icon me-btn-icon-lg"
        >
          <Button className="me-menu-item">Copy</Button>
          <Button className="me-menu-item">Export</Button>
        </Menu>
        <Button>Preview</Button>
        <Button className="me-btn me-btn-primary">Publish</Button>
      </div>
      <Button className="me-btn me-btn-secondary me-btn-block-mobile">
        Mobile full width
      </Button>
      <div className="reference-row">
        <label>
          <input
            type="checkbox"
            checked={dirty}
            onChange={event => setDirty(event.target.checked)}
          />{' '}
          Form dirty
        </label>
        <Button onClick={() => setCount(count + 1)}>Select row</Button>
      </div>
      <FloatingBarStack contentId="reference-content">
        <BulkActionToolbar count={count} onClear={() => setCount(0)}>
          <Button key="copy">Copy</Button>
          <Button key="export">Export</Button>
          <Button key="download">Download</Button>
          <Button key="apply" className="me-btn me-btn-primary">
            Apply
          </Button>
        </BulkActionToolbar>
        <StickySaveBar
          dirty={dirty}
          onSave={() => later().then(() => setDirty(false))}
          onDiscard={() => setDirty(false)}
        />
      </FloatingBarStack>
    </>
  )
}
